// The candidate pool of a customer group (/candidate-pool, staff only for now):
// one page of members, manual saves.
import type { Assignment } from "./assignments/assignment";
import { apiGet, apiPost } from "./fetch";

/** Why a person is a member. "replied", "wrote_first" and "sent_cv" are the
 * three kinds of Engaged. */
export const POOL_REASONS = [
	"applied",
	"in_process",
	"replied",
	"wrote_first",
	"sent_cv",
	"saved",
] as const;
export type PoolReason = (typeof POOL_REASONS)[number];

/** "Also search" sources: people who are not members, added to a search on request. */
export const ALSO_SOURCES = [
	"connection",
	"accepted_invite",
	"contacted_no_reply",
] as const;
export type AlsoSource = (typeof ALSO_SOURCES)[number];

export interface PoolRow {
	person_id: number;
	name: string;
	title: string | null;
	company: string | null;
	location: string | null;
	/** Empty for a prospect found by "Also search". */
	reasons: PoolReason[];
	sources: AlsoSource[];
	/** The detail each reason's chip shows after " · ", one per reason that
	 * has one; "wrote_first" and "sent_cv" have none. */
	reason_details: ReasonDetail[];
	saved: boolean;
	has_cv: boolean;
	last_touch: string;
	/** The CV fragment the search text matched; empty when only name or
	 * headline matched, or nothing was searched. */
	snippet: SnippetPart[];
}

/** What a reason's chip names: the role (or, for an application to a Jobylon
 * job with no role, the job's title) for applied / in_process / replied, the
 * saver's first name for saved. With a role filter on, the filtered role. */
export interface ReasonDetail {
	reason: Exclude<PoolReason, "wrote_first" | "sent_cv">;
	label: string;
	/** Further roles of the same reason beyond `label`, shown as "+N". */
	more: number;
}

/** A role of the group that appears on at least one member. */
export interface PoolRole {
	id: number;
	name: string;
}

export interface SnippetPart {
	text: string;
	/** One of the searched words. */
	match: boolean;
}

export interface PoolPage {
	/** A member's last touch is at most this many months old. */
	window_months: number;
	/** The whole pool, before any filter. */
	member_count: number;
	/** Rows matching the filters, prospects included. */
	count: number;
	page: number;
	page_size: number;
	/** The group's roles that appear on members, for the Role filter. */
	roles: PoolRole[];
	results: PoolRow[];
}

/** The `reasons` value that switches every member source off. */
export const NO_MEMBER_SOURCES = "none";

/** The reasons a present `reasons` query value switches on. Absent means all
 * of them. */
export const parseReasons = (value: string): PoolReason[] => {
	if (value === NO_MEMBER_SOURCES) return [];
	return POOL_REASONS.filter((reason) => value.split(",").includes(reason));
};

/** The `reasons` query value for the reasons switched on: null when all are. */
export const serializeReasons = (reasons: PoolReason[]): string | null => {
	if (reasons.length === POOL_REASONS.length) return null;
	return reasons.length === 0 ? NO_MEMBER_SOURCES : reasons.join(",");
};

export interface PoolFilters {
	/** The member sources switched on; all of them by default. */
	reasons: PoolReason[];
	also: AlsoSource[];
	months: number | null;
	location: string;
	q: string;
	/** Members who applied to, are in a process for, or replied about this role. */
	role: number | null;
}

/** The filters as the query string the page keeps in its URL. */
export const poolFilterParams = (filters: PoolFilters): URLSearchParams => {
	const params = new URLSearchParams();
	const reasons = serializeReasons(filters.reasons);
	if (reasons !== null) params.set("reasons", reasons);
	if (filters.also.length) params.set("also", filters.also.join(","));
	if (filters.months !== null) params.set("months", String(filters.months));
	if (filters.location) params.set("location", filters.location);
	if (filters.q) params.set("q", filters.q);
	if (filters.role !== null) params.set("role", String(filters.role));
	return params;
};

/** The sortable columns; a leading "-" is descending. */
export const POOL_ORDERS = [
	"last_touch",
	"-last_touch",
	"name",
	"-name",
	"role",
	"-role",
	"location",
	"-location",
	"why",
	"-why",
	"cv",
	"-cv",
] as const;
export type PoolOrder = (typeof POOL_ORDERS)[number];
export type PoolSortColumn = "last_touch" | "name" | "role" | "location" | "why" | "cv";

/** The order without an `order` param: newest last touch first. */
export const DEFAULT_POOL_ORDER: PoolOrder = "-last_touch";

/** The order after a click on `column`: the active column reverses, another
 * starts ascending (last contact newest first). Null is the default order. */
export const orderAfterClick = (
	current: PoolOrder | null,
	column: PoolSortColumn
): PoolOrder | null => {
	const active = current ?? DEFAULT_POOL_ORDER;
	let next: PoolOrder = column === "last_touch" ? "-last_touch" : column;
	if (active.replace(/^-/, "") === column) {
		next = active.startsWith("-") ? column : `-${column}`;
	}
	return next === DEFAULT_POOL_ORDER ? null : next;
};

/** One page of the pool; `filterQuery` is `poolFilterParams(filters).toString()`. */
export const fetchPoolPage = (
	group: number,
	filterQuery: string,
	page: number,
	order: PoolOrder | null
): Promise<PoolPage> => {
	const params = new URLSearchParams(filterQuery);
	params.set("group", String(group));
	params.set("page", String(page));
	if (order !== null) params.set("order", order);
	return apiGet(`/api/v2/candidate-pool/?${params}`) as Promise<PoolPage>;
};

export const fetchPoolSaved = (
	group: number,
	person: number
): Promise<{ saved: boolean }> =>
	apiGet(`/api/v2/candidate-pool/saved/?group=${group}&person=${person}`) as Promise<{
		saved: boolean;
	}>;

export const saveToPool = (group: number, person: number): Promise<unknown> =>
	apiPost("/api/v2/candidate-pool/save/", { group, person });

export const removeFromPool = (group: number, person: number): Promise<unknown> =>
	apiPost("/api/v2/candidate-pool/unsave/", { group, person });

/** An occupation the draft found among the picked people, with who holds it. */
export interface FoundRole {
	title: string;
	person_ids: number[];
}

/** Whether the picked people are one role and, if so, its drafted description. */
export interface PoolRoleDraft {
	verdict: "one_role" | "one_role_with_outliers" | "several_roles";
	roles: FoundRole[];
	/** Picked people who do not fit the role; left out of the description. */
	outlier_ids: number[];
	reason: string;
	/** Null for several roles: there is no one role to describe. */
	description: string | null;
}

/** The verdict on the picked people and, for one role, a description drafted
 * from their profiles and CVs. The group must be the signed-in user's own. */
export const draftPoolRoleDescription = (
	group: number,
	personIds: number[]
): Promise<PoolRoleDraft> =>
	apiPost("/api/v2/candidate-pool/role-description/", {
		group,
		person_ids: personIds,
	}) as Promise<PoolRoleDraft>;

export interface CreatedPoolRole {
	id: number;
	name: string;
	votes_created: number;
	/** Picked people who got no vote, such as a profile erased on request. */
	skipped: { person_id: number; reason: string }[];
}

/** Create the add-role wizard's role in the user's own group, with the picked
 * people as up-votes, in one request. */
export const createPoolAssignment = (
	group: number,
	personIds: number[],
	role: Partial<Assignment>
): Promise<CreatedPoolRole> =>
	apiPost("/api/v2/candidate-pool/create-assignment/", {
		...role,
		group,
		person_ids: personIds,
	}) as Promise<CreatedPoolRole>;
